/**
 * DTOs and types for Cash Transaction Events
 * Append-only event model for audit trail
 */

export type CashEventType = 
  | 'TillOpen' 
  | 'TillClose' 
  | 'Sale' 
  | 'Refund' 
  | 'PaidIn' 
  | 'PaidOut' 
  | 'CashDrop' 
  | 'FloatAdjustment'
  | 'Reconciliation'
  | 'VarianceInvestigation'
  | 'ManagerOverride'
  | 'BankDeposit';

export type ReconciliationStatus = 
  | 'Open'           // Till is open and active
  | 'Counting'       // User is declaring cash
  | 'Submitted'      // Submitted for review
  | 'Reconciled'     // Approved, no variance
  | 'VarianceUnderReview'  // Variance detected, under investigation
  | 'Closed';        // Final state

export type CashZoneType = 'Till' | 'Safe' | 'BackOffice' | 'Banking';

/**
 * Core cash event - append-only, never edited or deleted
 */
export interface CashEventDto {
  id: number;
  eventType: CashEventType;
  storeNumber: string;
  tillNumber?: string;          // For till-specific events
  zoneId?: number;              // Links to LocationResponse zones
  amount: number;
  currencyCode: string;
  userId: string;
  username: string;
  timestamp: string;
  shiftId?: string;             // Link to operational shift
  referenceId?: string;         // POS transaction ID
  documentId?: number;          // Link to CCTV/evidence
  reversalOfEventId?: number;   // For corrections (points to reversed event)
  isReversed: boolean;          // Marked if this event was reversed
  notes?: string;
  metadata: Record<string, unknown>; // Event-specific data
  createdAtUtc: string;
}

/**
 * Till/Cash zone configuration
 */
export interface CashZoneDto {
  id: number;
  storeNumber: string;
  zoneName: string;             // "Till 1", "Safe", "Back Office"
  zoneType: CashZoneType;
  isActive: boolean;
  assignedUserId?: string;
  assignedUsername?: string;
  requiresTwoPersonApproval: boolean;
  varianceThresholdAmount: number;  // Variance requiring manager approval
  rowNumber?: number;           // Display order
}

/**
 * Denomination breakdown for cash counts
 */
export interface DenominationBreakdownDto {
  notes: { [denomination: string]: number };  // { "50": 2, "20": 5, "10": 10 }
  coins: { [denomination: string]: number };  // { "2": 5, "1": 10, "0.50": 20 }
  totalNotes: number;
  totalCoins: number;
  grandTotal: number;
}

/**
 * Cash reconciliation session (similar to stock count)
 */
export interface CashReconciliationDto {
  id: number;
  storeNumber: string;
  tillNumber: string;
  zoneId: number;
  reconciliationDate: string;
  shiftId?: string;
  status: ReconciliationStatus;
  
  // Opening state
  openingBalance: number;
  openingTimestamp: string;
  openedByUserId: string;
  openedByUsername: string;
  
  // Expected values (from POS/system)
  expectedCashSales: number;
  expectedTotalSales: number;
  expectedCardSales: number;
  expectedRefunds: number;
  expectedPaidIns: number;
  expectedPaidOuts: number;
  expectedCashDrops: number;
  expectedClosingBalance: number;
  
  // Declared values (manual count)
  declaredClosingBalance?: number;
  declaredTimestamp?: string;
  declaredByUserId?: string;
  declaredByUsername?: string;
  denominationBreakdown?: DenominationBreakdownDto;
  countMethod: 'QuickTotal' | 'DenominationBreakdown';
  
  // Variance
  variance: number;
  variancePercentage: number;
  isOverThreshold: boolean;
  
  // Approval
  requiresApproval: boolean;
  approvedByUserId?: string;
  approvedByUsername?: string;
  approvedTimestamp?: string;
  
  // Investigation
  investigationNotes?: string;
  investigationStatus?: 'Pending' | 'InProgress' | 'Resolved';
  
  notes?: string;
  createdAtUtc: string;
  updatedAtUtc: string;
}

/**
 * Till lifecycle state
 */
export interface TillStateDto {
  tillNumber: string;
  zoneId: number;
  storeNumber: string;
  status: ReconciliationStatus;
  currentBalance: number;
  lastReconciliationId?: number;
  lastReconciliationTimestamp?: string;
  currentShiftId?: string;
  assignedUserId?: string;
  assignedUsername?: string;
  isLocked: boolean;              // Prevents new transactions
}

/**
 * Expected cash calculation from POS
 */
export interface ExpectedCashCalculationDto {
  storeNumber: string;
  tillNumber: string;
  startTimestamp: string;
  endTimestamp: string;
  openingBalance: number;
  cashSales: number;
  cardSales: number;
  refunds: number;
  paidIns: number;
  paidOuts: number;
  cashDrops: number;
  expectedClosing: number;
  transactionCount: number;
}

/**
 * Request DTOs
 */
export interface CreateCashEventRequest {
  eventType: CashEventType;
  storeNumber: string;
  tillNumber?: string;
  zoneId?: number;
  amount: number;
  shiftId?: string;
  referenceId?: string;
  notes?: string;
  metadata?: Record<string, unknown>;
}

export interface OpenTillRequest {
  storeNumber: string;
  tillNumber: string;
  zoneId: number;
  openingBalance: number;
  shiftId?: string;
  denominationBreakdown?: DenominationBreakdownDto;
  notes?: string;
}

export interface CloseTillRequest {
  reconciliationId: number;
  declaredClosingBalance: number;
  denominationBreakdown?: DenominationBreakdownDto;
  countMethod: 'QuickTotal' | 'DenominationBreakdown';
  notes?: string;
}

export interface ApproveCashReconciliationRequest {
  reconciliationId: number;
  approvalNotes?: string;
  overrideVariance?: boolean;
}

export interface CreateCashDropRequest {
  storeNumber: string;
  fromTillNumber: string;
  toZoneId: number;  // Usually the safe
  amount: number;
  referenceId?: string;
  notes?: string;
}

export interface ReverseCashEventRequest {
  eventIdToReverse: number;
  reason: string;
  notes?: string;
}

/**
 * Helper functions
 */
export const calculateVariance = (expected: number, declared: number): number => {
  return declared - expected;
};

export const calculateVariancePercentage = (expected: number, declared: number): number => {
  if (expected === 0) return declared === 0 ? 0 : 100;
  return ((declared - expected) / expected) * 100;
};

export const isVarianceOverThreshold = (variance: number, threshold: number): boolean => {
  return Math.abs(variance) > threshold;
};

export const formatCashAmount = (amount: number, currencyCode: string = 'GBP'): string => {
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: currencyCode,
  }).format(amount);
};

export const getReconciliationStatusColor = (status: ReconciliationStatus): string => {
  switch (status) {
    case 'Open':
      return 'info';
    case 'Counting':
      return 'warning';
    case 'Submitted':
      return 'primary';
    case 'Reconciled':
      return 'success';
    case 'VarianceUnderReview':
      return 'error';
    case 'Closed':
      return 'default';
    default:
      return 'default';
  }
};

export const getCashEventTypeLabel = (eventType: CashEventType): string => {
  const labels: Record<CashEventType, string> = {
    TillOpen: 'Till Opened',
    TillClose: 'Till Closed',
    Sale: 'Cash Sale',
    Refund: 'Refund',
    PaidIn: 'Paid In',
    PaidOut: 'Paid Out',
    CashDrop: 'Cash Drop',
    FloatAdjustment: 'Float Adjustment',
    Reconciliation: 'Reconciliation',
    VarianceInvestigation: 'Variance Investigation',
    ManagerOverride: 'Manager Override',
    BankDeposit: 'Bank Deposit',
  };
  return labels[eventType] || eventType;
};

/**
 * UK currency denominations
 */
export const UK_NOTE_DENOMINATIONS = ['50', '20', '10', '5'];
export const UK_COIN_DENOMINATIONS = ['2', '1', '0.50', '0.20', '0.10', '0.05', '0.02', '0.01'];

export const calculateDenominationTotal = (breakdown: DenominationBreakdownDto): number => {
  let total = 0;
  
  Object.entries(breakdown.notes).forEach(([denom, count]) => {
    total += parseFloat(denom) * count;
  });
  
  Object.entries(breakdown.coins).forEach(([denom, count]) => {
    total += parseFloat(denom) * count;
  });
  
  return total;
};
